import React, { useEffect, useState } from 'react';
import {
    FiX,
    FiCheckCircle,
    FiMail,
    FiClock,
    FiUser,
    FiPhone,
    FiShield,
    FiCopy,
    FiCheck,
    FiPhoneCall,
    FiSend,
    FiRefreshCw,
    FiFileText,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import toast from 'react-hot-toast';
import {
    BUSINESS_EMAIL,
    BUSINESS_PHONE_MAIN,
    BUSINESS_WHATSAPP_CHAT_URL,
} from '../../config/businessInfo';
import './MailboxModal.css';

const RAHBER_LOGO_URL = 'https://res.cloudinary.com/fl3tts9v/image/upload/v1790969119/Logo-Photoroom_a47mr1.png';

const MailboxModal = ({
    isOpen,
    onClose,
    formData,
    referenceId,
}) => {
    const [copied, setCopied] = useState(false);
    const [currentTime, setCurrentTime] = useState('');
    const [activeTab, setActiveTab] = useState('animation'); // 'animation' | 'receipt'
    const [animKey, setAnimKey] = useState(0);

    useEffect(() => {
        if (!isOpen) {
            setActiveTab('animation');
            return undefined;
        }

        const now = new Date();
        const formatted = now.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
        setCurrentTime(`${formatted} (PKT)`);

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const refCode = referenceId || formData?.referenceId || 'RC-948210';
    const senderName = formData?.name || 'Valued Customer';
    const senderEmail = formData?.email || 'customer@gmail.com';
    const senderPhone = formData?.phone || '0300-XXXXXXX';
    const inquirySubject = formData?.subject || 'Showroom Appliance Inquiry';
    const inquiryMessage = formData?.message || 'Inquiry details submitted via Rahber Corporation online contact desk.';

    const whatsappMessage = `Hello Rahber Corporation! I just submitted an inquiry on your website.\n\n*Reference ID:* #${refCode}\n*Name:* ${senderName}\n*Phone:* ${senderPhone}\n*Subject:* ${inquirySubject}\n\nPlease update me on stock, pricing, and installment details.`;

    const handleCopyRef = () => {
        navigator.clipboard?.writeText?.(refCode);
        setCopied(true);
        toast.success(`Reference #${refCode} copied to clipboard!`);
        setTimeout(() => setCopied(false), 2500);
    };

    const handleReplayAnimation = () => {
        setAnimKey((prev) => prev + 1);
    };

    return (
        <div
            className="mailbox-overlay"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Official Mailbox Dispatch Confirmation"
        >
            <div
                className="mailbox-window"
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── Top Mail Client Titlebar ── */}
                <div className="mailbox-titlebar">
                    <div className="mailbox-titlebar__left">
                        <span className="mailbox-traffic-dot mailbox-traffic-dot--red" onClick={onClose} title="Close" />
                        <span className="mailbox-traffic-dot mailbox-traffic-dot--yellow" />
                        <span className="mailbox-traffic-dot mailbox-traffic-dot--green" />
                        <div className="mailbox-client-badge">
                            <FiMail className="mailbox-client-icon" />
                            <span className="mailbox-client-text">Rahber Official Webmail</span>
                        </div>
                    </div>

                    {/* View Switcher Tabs */}
                    <div className="mailbox-tab-switcher">
                        <button
                            type="button"
                            className={`mailbox-tab-btn ${activeTab === 'animation' ? 'active' : ''}`}
                            onClick={() => setActiveTab('animation')}
                        >
                            📮 Mailbox Animation
                        </button>
                        <button
                            type="button"
                            className={`mailbox-tab-btn ${activeTab === 'receipt' ? 'active' : ''}`}
                            onClick={() => setActiveTab('receipt')}
                        >
                            <FiFileText /> Official Receipt
                        </button>
                    </div>

                    <div className="mailbox-titlebar__right">
                        <span className="mailbox-verified-badge" title="Verified Showroom Inquiry">
                            <FiCheckCircle /> Verified Dispatch
                        </span>
                        <button
                            type="button"
                            className="mailbox-close-btn"
                            onClick={onClose}
                            aria-label="Close mailbox dialog"
                            title="Close (Esc)"
                        >
                            <FiX />
                        </button>
                    </div>
                </div>

                {/* ── TAB 1: 3D ANIMATED MAILBOX VIEW ── */}
                {activeTab === 'animation' && (
                    <div className="mailbox-anim-view" key={animKey}>
                        <div className="mailbox-anim-stage">
                            {/* Sparks / Particles */}
                            <div className="mailbox-sparkles" aria-hidden="true">
                                <span className="m-spark s1">✦</span>
                                <span className="m-spark s2">★</span>
                                <span className="m-spark s3">✦</span>
                                <span className="m-spark s4">★</span>
                            </div>

                            {/* Curbside Post & Mailbox 3D Container */}
                            <div className="physical-mailbox-container">
                                {/* Flying Envelope that drops inside */}
                                <div className="flying-mail-envelope">
                                    <div className="envelope-inner">
                                        <div className="envelope-stamp">
                                            <span>RC</span>
                                        </div>
                                        <div className="envelope-meta-lines">
                                            <span className="env-to">TO: RAHBER SHOWROOM</span>
                                            <span className="env-ref">#{refCode}</span>
                                        </div>
                                        <div className="envelope-seal" />
                                    </div>
                                </div>

                                {/* 3D Mailbox SVG Body with Post, Door and Flag */}
                                <div className="mailbox-hardware">
                                    <svg
                                        viewBox="0 0 240 210"
                                        className="mailbox-hardware-svg"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <defs>
                                            <linearGradient id="bodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="#1e293b" />
                                                <stop offset="40%" stopColor="#334155" />
                                                <stop offset="100%" stopColor="#0f172a" />
                                            </linearGradient>
                                            <linearGradient id="postGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                                <stop offset="0%" stopColor="#78350f" />
                                                <stop offset="50%" stopColor="#92400e" />
                                                <stop offset="100%" stopColor="#5a2307" />
                                            </linearGradient>
                                            <linearGradient id="flagGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="#ef4444" />
                                                <stop offset="100%" stopColor="#b91c1c" />
                                            </linearGradient>
                                            <filter id="boxShadow" x="-10%" y="-10%" width="130%" height="130%">
                                                <feDropShadow dx="0" dy="8" stdDeviation="6" floodOpacity="0.5" />
                                            </filter>
                                        </defs>

                                        {/* Wooden Curb Post */}
                                        <rect x="110" y="125" width="22" height="85" rx="3" fill="url(#postGrad)" />
                                        <polygon points="106,125 136,125 128,140 114,140" fill="#451a03" />

                                        {/* Mailbox Tunnel Body */}
                                        <g filter="url(#boxShadow)">
                                            <rect x="50" y="45" width="140" height="75" rx="37.5" fill="url(#bodyGrad)" stroke="#475569" strokeWidth="2.5" />
                                            {/* Floor plate */}
                                            <path d="M 50 82.5 L 50 120 L 190 120 L 190 82.5 Z" fill="#0f172a" opacity="0.6" />
                                        </g>

                                        {/* Mailbox Chamber Interior Slot */}
                                        <ellipse cx="60" cy="82.5" rx="14" ry="34" fill="#050a14" stroke="#1e293b" strokeWidth="2" />

                                        {/* Hinged Front Door with Handle */}
                                        <g className="mailbox-door-assembly">
                                            <ellipse cx="56" cy="82.5" rx="14" ry="34" fill="#1e293b" stroke="#64748b" strokeWidth="2.5" />
                                            {/* Door handle latch */}
                                            <circle cx="56" cy="65" r="4.5" fill="#facc15" stroke="#ca8a04" strokeWidth="1.5" />
                                        </g>

                                        {/* Red Signal Flag that pops up */}
                                        <g className="mailbox-signal-flag">
                                            {/* Flag stick pivoted at (165, 82) */}
                                            <rect x="163" y="48" width="5" height="38" rx="2.5" fill="#e2e8f0" />
                                            {/* Red flag vane */}
                                            <rect x="163" y="32" width="28" height="18" rx="3" fill="url(#flagGrad)" stroke="#dc2626" strokeWidth="1" />
                                            {/* Pivot rivet */}
                                            <circle cx="165.5" cy="84" r="4.5" fill="#94a3b8" />
                                        </g>

                                        {/* Official Corporation Decal on Side */}
                                        <text x="100" y="86" fill="#f8fafc" fontSize="9.5" fontWeight="700" letterSpacing="0.08em" opacity="0.9">
                                            RAHBER CORP
                                        </text>
                                        <text x="100" y="98" fill="#38bdf8" fontSize="7" fontWeight="600" opacity="0.85">
                                            SHOWROOM MAIL
                                        </text>
                                    </svg>
                                </div>
                            </div>

                            {/* Dispatched Confirmation Callout */}
                            <div className="mailbox-anim-success">
                                <div className="anim-badge-row">
                                    <span className="anim-check-circle">
                                        <FiCheckCircle />
                                    </span>
                                    <div className="anim-text-group">
                                        <h3>Inquiry Dispatched &amp; Logged!</h3>
                                        <p>
                                            Your message has successfully dropped into Rahber Corporation's official showroom mailbox queue.
                                        </p>
                                    </div>
                                </div>

                                <div className="anim-reference-pill">
                                    <span className="ref-label">Official Reference:</span>
                                    <strong className="ref-num">#{refCode}</strong>
                                    <button
                                        type="button"
                                        className="anim-copy-btn"
                                        onClick={handleCopyRef}
                                        title="Copy reference code"
                                    >
                                        {copied ? <FiCheck /> : <FiCopy />}
                                    </button>
                                </div>

                                <div className="anim-portal-note">
                                    <span className="portal-indicator-dot" />
                                    <span>
                                        Registered with <strong>Lahore Showroom Dispatch</strong> • Showroom sales desk notified
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Action buttons under animation */}
                        <div className="mailbox-anim-actions">
                            <button
                                type="button"
                                className="anim-btn anim-btn--receipt"
                                onClick={() => setActiveTab('receipt')}
                            >
                                <FiFileText /> View Official Receipt
                            </button>

                            <a
                                href={`${BUSINESS_WHATSAPP_CHAT_URL}?text=${encodeURIComponent(whatsappMessage)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="anim-btn anim-btn--wa"
                            >
                                <FaWhatsapp /> Direct WhatsApp Follow-up
                            </a>

                            <button
                                type="button"
                                className="anim-btn anim-btn--replay"
                                onClick={handleReplayAnimation}
                                title="Replay mailbox drop animation"
                            >
                                <FiRefreshCw /> Replay
                            </button>
                        </div>
                    </div>
                )}

                {/* ── TAB 2: OFFICIAL RFC822 RECEIPT VIEW ── */}
                {activeTab === 'receipt' && (
                    <>
                        {/* ── Email Envelope Header (RFC822 Styled) ── */}
                        <div className="mailbox-envelope-header">
                            <div className="mailbox-header-row mailbox-header-row--subject">
                                <span className="mailbox-header-label">Subject:</span>
                                <span className="mailbox-subject-text">{inquirySubject}</span>
                            </div>

                            <div className="mailbox-header-grid">
                                <div className="mailbox-header-row">
                                    <span className="mailbox-header-label">From:</span>
                                    <div className="mailbox-sender-pill">
                                        <span className="mailbox-sender-avatar">
                                            {senderName.charAt(0).toUpperCase()}
                                        </span>
                                        <span className="mailbox-sender-name">{senderName}</span>
                                        <span className="mailbox-sender-email">&lt;{senderEmail}&gt;</span>
                                    </div>
                                </div>

                                <div className="mailbox-header-row">
                                    <span className="mailbox-header-label">To:</span>
                                    <div className="mailbox-recipient-pill">
                                        <span className="mailbox-recipient-name">Rahber Corporation Showroom</span>
                                        <span className="mailbox-recipient-email">&lt;{BUSINESS_EMAIL}&gt;</span>
                                        <span className="mailbox-verified-badge" title="Official Verified Desk">
                                            <FiCheckCircle /> Verified Showroom
                                        </span>
                                    </div>
                                </div>

                                <div className="mailbox-header-meta">
                                    <span className="mailbox-meta-item">
                                        <FiClock /> {currentTime || 'Just Now'}
                                    </span>
                                    <span className="mailbox-meta-item mailbox-meta-security">
                                        <FiShield /> TLS 1.3 256-bit Encrypted
                                    </span>
                                    <button
                                        type="button"
                                        className="mailbox-ref-copy-btn"
                                        onClick={handleCopyRef}
                                        title="Copy Reference ID"
                                    >
                                        {copied ? <FiCheck /> : <FiCopy />}
                                        <span>Ref: #{refCode}</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* ── Official HTML Email Message Body ── */}
                        <div className="mailbox-body-scroll">
                            <div className="mailbox-email-sheet">
                                {/* Letterhead */}
                                <div className="mailbox-letterhead">
                                    <div className="mailbox-letterhead__brand">
                                        <img
                                            src={RAHBER_LOGO_URL}
                                            alt="Rahber Corporation"
                                            className="mailbox-letterhead__logo"
                                        />
                                        <div className="mailbox-letterhead__titles">
                                            <h3 className="mailbox-letterhead__name">Rahber Corporation</h3>
                                            <p className="mailbox-letterhead__sub">
                                                Flagship Home Appliances Retailer Since 1980
                                            </p>
                                            <p className="mailbox-letterhead__addr">
                                                Main Bazar Chandni Chowk, Sanda Kalan, Lahore, Pakistan
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mailbox-letterhead__stamp">
                                        <div className="mailbox-stamp-box">
                                            <span className="mailbox-stamp-title">OFFICIAL DISPATCH</span>
                                            <span className="mailbox-stamp-code">#{refCode}</span>
                                            <span className="mailbox-stamp-date">STATUS: LOGGED</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mailbox-divider" />

                                {/* Salutation */}
                                <div className="mailbox-salutation">
                                    <strong>Assalam-o-Alaikum {senderName},</strong>
                                    <p>
                                        Thank you for contacting Rahber Corporation. Your inquiry has been dispatched to our Lahore showroom management desk and senior appliance specialists.
                                    </p>
                                </div>

                                {/* Customer's Message Block */}
                                <div className="mailbox-quote-box">
                                    <div className="mailbox-quote-header">
                                        <FiSend />
                                        <span>Message Content Sent By Customer:</span>
                                    </div>
                                    <blockquote className="mailbox-quote-text">
                                        {inquiryMessage}
                                    </blockquote>
                                </div>

                                {/* Verified Inquiry Details Card */}
                                <div className="mailbox-details-card">
                                    <h4 className="mailbox-details-title">
                                        <FiCheckCircle /> Verified Dispatch Specifications
                                    </h4>
                                    <div className="mailbox-details-grid">
                                        <div className="mailbox-detail-item">
                                            <span className="mailbox-detail-k"><FiUser /> Full Name</span>
                                            <strong className="mailbox-detail-v">{senderName}</strong>
                                        </div>
                                        <div className="mailbox-detail-item">
                                            <span className="mailbox-detail-k"><FiPhone /> Contact Phone</span>
                                            <strong className="mailbox-detail-v">{senderPhone}</strong>
                                        </div>
                                        <div className="mailbox-detail-item">
                                            <span className="mailbox-detail-k"><FiMail /> Email Address</span>
                                            <strong className="mailbox-detail-v">{senderEmail}</strong>
                                        </div>
                                        <div className="mailbox-detail-item">
                                            <span className="mailbox-detail-k">🏢 Assigned Showroom</span>
                                            <strong className="mailbox-detail-v">Sanda Kalan Flagship, Lahore</strong>
                                        </div>
                                        <div className="mailbox-detail-item">
                                            <span className="mailbox-detail-k">⏱ Response Turnaround</span>
                                            <strong className="mailbox-detail-v">Within 15 to 30 Mins (Business Hours)</strong>
                                        </div>
                                        <div className="mailbox-detail-item">
                                            <span className="mailbox-detail-k">📋 Priority Status</span>
                                            <strong className="mailbox-detail-v" style={{ color: '#16A34A' }}>
                                                High Priority Showroom Queue
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                {/* Reassurance Urdu Comfort Box */}
                                <div className="mailbox-comfort-banner">
                                    <div className="mailbox-comfort-banner__icon">✓</div>
                                    <div className="mailbox-comfort-banner__text">
                                        <strong>Aap Bilkul Befikar Rahain!</strong>
                                        <span>
                                            Aap ki inquiry hamare showroom records me 100% save ho chuki hai. Agar aap ko foran instant deal, installment quotation ya doorstep delivery confirm karni hai to neechay diye gaye direct WhatsApp button par click karein:
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Bottom Action Footer ── */}
                        <div className="mailbox-footer">
                            <div className="mailbox-footer__actions">
                                <a
                                    href={`${BUSINESS_WHATSAPP_CHAT_URL}?text=${encodeURIComponent(whatsappMessage)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mailbox-btn-whatsapp"
                                >
                                    <FaWhatsapp />
                                    <span>Instant WhatsApp Follow-up (Ref #{refCode})</span>
                                </a>

                                <a
                                    href={`tel:${Array.isArray(BUSINESS_PHONE_MAIN) ? BUSINESS_PHONE_MAIN[0].tel : BUSINESS_PHONE_MAIN}`}
                                    className="mailbox-btn-call"
                                >
                                    <FiPhoneCall />
                                    <span>Call Showroom Desk</span>
                                </a>

                                <button
                                    type="button"
                                    className="mailbox-btn-close"
                                    onClick={onClose}
                                >
                                    Done / Return to Store
                                </button>
                            </div>

                            <div className="mailbox-footer__subtext">
                                <span>Showroom Helpline: 0313-1451223 • Sanda Kalan, Lahore • Mon to Sat 11 AM to 8 PM</span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MailboxModal;
